// src/components/OPDRejectedMembers.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Search, X, Download, FilePenLine, Trash2 } from "lucide-react";
import MemberForm from "./MemberForm";

const API_BASE_URL = "https://aaapd.org.et/gallery_backend/api";
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, "") + "/self_registered_attachments";

export default function OPDRejectedMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [opdSubCity, setOpdSubCity] = useState("");

  const detailModalRef = useRef(null);

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext);
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));
      const currentUserEmail = currentUser?.email;
      if (!currentUserEmail) {
        setMembers([]);
        setLoading(false);
        return;
      }
      // Fetch OPD profile to get sub city
      let subCity = opdSubCity;
      if (!subCity) {
        const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
        const profData = await profRes.json();
        if (profData?.status === 'success' && profData?.data?.sub_city) {
          subCity = profData.data.sub_city;
          setOpdSubCity(subCity);
        } else {
          setMembers([]);
          setLoading(false);
          return;
        }
      }
      // Fetch all members from getAllMembers.php
      const response = await fetch(`${API_BASE_URL}/getAllMembers.php`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      // Filter by sub city and rejected status
      const norm = (s) => (s || '').toString().trim().toLowerCase();
      const subCityNorm = norm(subCity);
      const filtered = list.filter((m) => 
        norm(m.subCity) === subCityNorm && 
        norm(m.aaapd_status) === "rejected"
      );
      setMembers(filtered);
    } catch (e) {
      console.error("Failed to fetch members:", e);
      setMembers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const fullName = `${member.firstName} ${member.middleName || ""} ${member.lastName}`.toLowerCase();
      if (
        searchTerm &&
        !fullName.includes(searchTerm.toLowerCase()) &&
        !((member.idNumber || member.uniqueID || "").toString().toLowerCase().includes(searchTerm.toLowerCase()))
      ) {
        return false;
      }
      return true;
    });
  }, [members, searchTerm]);

  const handleDetail = (member) => {
    setDetailMember(member);
    setShowDetails(true);
  };
  const closeDetailModal = () => {
    setShowDetails(false);
    setDetailMember(null);
  };

  const onFormClose = () => {
    setShowForm(false);
    setEditingMember(null);
  };

  const onMemberAdded = () => {
    fetchMembers();
    setEditingMember(null);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      const user = JSON.parse(localStorage.getItem("currentUser"));
      if (!user || !user.email) {
        throw new Error('User not authenticated');
      }
      const response = await fetch(`${API_BASE_URL}/deleteMember.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, requestEmail: user.email })
      });
      const data = await response.json();
      if (data.status === 'success') {
        fetchMembers();
        alert("Member deleted successfully and moved to Trash");
      } else {
        throw new Error(data.message || 'Failed to delete member');
      }
    } catch (error) {
      console.error("Error deleting member:", error);
      alert(error.message || "Failed to delete member");
    }
  };

  const exportToCSV = () => {
    if (filteredMembers.length === 0) {
      alert("No members to export");
      return;
    }
    const headers = [
      "Full Name",
      "Gender",
      "Date of Birth",
      "Age",
      "Phone Number",
      "Email",
      "Location",
      "Sub City",
      "Woreda",
      "House Number",
      "Disability Type",
      "Education Level",
      "Marital Status",
      "Employment Status",
      "Member Status",
      "ID Number",
    ];
    const rows = filteredMembers.map((m) => [
      `${m.firstName} ${m.middleName ? m.middleName + " " : ""}${m.lastName}`,
      m.gender || "",
      m.dateOfBirth || "",
      m.age || "",
      m.phoneNumber || "",
      m.AltPhoneNumber || "",
      m.email || "",
      m.location || "",
      m.subCity || "",
      m.woreda || "",
      m.houseNumber || "",
      m.disabilityType || "",
      m.educationLevel || "",
      m.maritalStatus || "",
      m.employmentStatus || "",
      m.aaapd_status || "",
      m.uniqueID || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `opd_rejected_members_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Rejected Members</h1>
          <p className="text-sm text-gray-500 mt-1">Members with status Rejected in your OPD.</p>
        </header>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search members (name or ID)"
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search members"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              onClick={exportToCSV}
              disabled={filteredMembers.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to CSV"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Attachment Lightbox Modal */}
        {attachmentPreview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
            <div className="relative max-w-5xl max-h-[90vh] w-auto m-4">
              <button
                type="button"
                className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full shadow p-2 hover:bg-gray-100"
                onClick={() => setAttachmentPreview(null)}
                aria-label="Close attachment preview"
              >
                <X size={20} />
              </button>
              <img
                src={attachmentPreview}
                alt="Attachment Preview"
                className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading rejected members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No rejected members found.</p>
            </div>
          ) : (
            <>
              {/* Table for md+ */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider w-12">#</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Full Name</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Gender</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">ID Number</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Membership Status</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Rejection Reason</th>
                      <th className="py-3 px-6 text-center font-semibold text-gray-600 text-sm uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredMembers.map((member, index) => (
                      <tr key={member.id} className="hover:bg-gray-50 transition">
                        <td className="py-4 px-4 whitespace-nowrap text-gray-600">{index + 1}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{`${member.firstName} ${member.middleName ? member.middleName + " " : ""}${member.lastName}`}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{member.gender}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{member.uniqueID || '-'}</td>
                        <td className="py-4 px-6 whitespace-nowrap">
                          {(() => {
                            const v = (member.aaapd_status || '').toString();
                            const s = v.toLowerCase();
                            const cls = s === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : s === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : s === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800';
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                                {v || '-'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-700 max-w-xs truncate" title={member.rejection_reason || ''}>
                          {member.rejection_reason || '-'}
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleDetail(member)}
                              className="text-teal-600 hover:text-teal-700 font-medium"
                              title="View Details"
                            >
                              Details
                            </button>
                            <button
                              onClick={() => handleEdit(member)}
                              className="text-gray-500 hover:text-blue-600"
                              title="Edit Member"
                            >
                              <FilePenLine size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(member.id)}
                              className="text-gray-500 hover:text-red-600"
                              title="Delete Member"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cards for small screens */}
              <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                {filteredMembers.map((member) => (
                  <div key={member.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col justify-between">
                    <div>
                      <h4 className="font-bold text-lg text-gray-800">{`${member.firstName} ${member.lastName}`}</h4>
                      <p className="text-sm text-gray-600">ID: {member.uniqueID || '-'}</p>
                      <p className="text-sm text-gray-600">{member.gender}</p>
                      {member.rejection_reason && (
                        <p className="mt-2 text-sm text-red-700"><span className="font-semibold">Reason:</span> {member.rejection_reason}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-3 mt-4">
                      <button
                        onClick={() => handleDetail(member)}
                        className="text-teal-600 hover:text-teal-700 font-medium"
                        title="View Details"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => handleEdit(member)}
                        className="text-gray-500 hover:text-blue-600"
                        title="Edit Member"
                      >
                        <FilePenLine size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(member.id)}
                        className="text-gray-500 hover:text-red-600"
                        title="Delete Member"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Details Modal (same as MembersPage style) */}
        {showDetails && detailMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
            <div ref={detailModalRef} className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
                <h3 className="text-xl font-semibold text-gray-800">Member Details</h3>
                <div className="flex items-center gap-3">
                  {detailMember.photoAttachment && (
                    isImageFile(detailMember.photoAttachment) ? (
                      <button
                        type="button"
                        className="group"
                        title="View passport photo"
                        onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                      >
                        <img
                          src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          alt="Passport Photo"
                          className="h-24 w-20 rounded object-cover object-top border border-gray-200 shadow-sm group-hover:opacity-90"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                    ) : (
                      <a
                        href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 hover:underline"
                      >
                        Passport Photo
                      </a>
                    )
                  )}
                  <button
                    type="button"
                    className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                    onClick={closeDetailModal}
                    aria-label="Close details"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      { label: "Full Name", value: `${detailMember.firstName} ${detailMember.middleName || ""} ${detailMember.lastName}` },
                      { label: "Gender", value: detailMember.gender },
                      { label: "Date of Birth", value: detailMember.dateOfBirth },
                      { label: "Age", value: detailMember.age },
                      { label: "Phone Number", value: detailMember.phoneNumber },
                      { label: "Alternative Phone Number", value: detailMember.AltPhoneNumber },
                      { label: "Email", value: detailMember.email },
                      { label: "Location", value: detailMember.location },
                      { label: "Sub City", value: detailMember.subCity },
                      { label: "Woreda", value: detailMember.woreda },
                      { label: "House Number", value: detailMember.houseNumber },
                      { label: "Specific Location", value: detailMember.specificLocation },
                      { label: "Disability Type", value: detailMember.disabilityType === "Other" ? detailMember.disabilityOther || "Other" : detailMember.disabilityType },
                      { label: "Disability Description", value: detailMember.disabilityDescription },
                      { label: "Education Level", value: detailMember.educationLevel },
                      { label: "Marital Status", value: detailMember.maritalStatus },
                      { label: "Number of Families", value: detailMember.numberOfFamilies },
                      { label: "Employment Status", value: detailMember.employmentStatus },
                      { label: "Member Status", value: detailMember.aaapd_status },
                      { label: "Rejection Reason", value: detailMember.rejection_reason },
                      { label: "Labour ID", value: detailMember.labourID },
                      { label: "Labour ID Number", value: detailMember.labourIDNumber },
                      { label: "Place of Work", value: detailMember.workPlace },
                      { label: "Name of the Office", value: detailMember.officeName },
                      { label: "Type of the Work", value: detailMember.workType },
                      { label: "Job Responsibilities", value: detailMember.jobResponsibility },
                      { label: "Support Needed", value: detailMember.supportNeeded },
                      { label: "Work Needed", value: detailMember.workNeeded },
                      { label: "Support Received", value: detailMember.supportReceived },
                      { label: "ID Type", value: detailMember.idType },
                      { label: "ID Number", value: detailMember.idNumber },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-sm font-medium text-gray-500">{label}</p>
                        {label === "Member Status" ? (
                          (() => {
                            const v = (value || "").toString();
                            const s = v.toLowerCase();
                            const cls = s === "approved"
                              ? "bg-green-100 text-green-800"
                              : s === "rejected"
                              ? "bg-red-100 text-red-800"
                              : s === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800";
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                                {v || "-"}
                              </span>
                            );
                          })()
                        ) : (
                          <p className="text-base text-gray-800 font-semibold">{value || "-"}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* ID Attachment Preview */}
                  {detailMember.idAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Receipt Attachment</p>
                      {isImageFile(detailMember.idAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.idAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                            alt="Receipt Attachment"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Attachment
                        </a>
                      )}
                    </div>
                  )}

                  {/* Passport Photo Preview */}
                  {detailMember.photoAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Passport Photo</p>
                      {isImageFile(detailMember.photoAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                            alt="Passport Photo"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Photo
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-white rounded-b-xl">
                <button
                  type="button"
                  onClick={closeDetailModal}
                  className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Add/Edit Member Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
          <div className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-2xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
              <h3 id="member-form-title" className="text-xl font-semibold text-gray-800">
                {editingMember ? 'Edit Member' : 'Add Member'}
              </h3>
              <button
                type="button"
                className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                onClick={onFormClose}
                aria-label="Close form"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              {(() => {
                const currentUser = JSON.parse(localStorage.getItem("currentUser"));
                const currentUserEmail = currentUser?.email || "";
                return (
                  <MemberForm editingMember={editingMember} currentUserEmail={currentUserEmail} onMemberAdded={onMemberAdded} onClose={onFormClose} />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 
